import { useEffect, useMemo, useState } from 'react';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';

import useRestaurantWaste from '../../../hooks/restaurant/useRestaurantWaste';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import { RestaurantWasteReason } from '../../../types/restaurant/RestaurantCatalogExtensionTypes';

import {
  WasteBranchEmpty,
  WasteEmpty,
  WasteHeader,
  WasteFilters,
  WasteSkeleton,
  WasteTable,
} from './components';
import WasteFormModal from './WasteFormModal';

const RestaurantWasteScreen = () => {
  const waste = useRestaurantWaste();
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);
  const [reason, setReason] = useState<RestaurantWasteReason | 'ALL'>('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const debouncedSearch = useDebouncedValue(search, 350);
  const filters = useMemo(() => ({
    search: debouncedSearch.trim() || undefined,
    categoryId: categoryId || undefined,
    reason: reason === 'ALL' ? undefined : reason,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    size,
  }), [categoryId, dateFrom, dateTo, debouncedSearch, page, reason, size]);
  const categories = useMemo(() => Array.from(
    new Map(waste.inventory.map((item) => [item.categoryId, { id: item.categoryId, name: item.categoryName }])).values()
  ), [waste.inventory]);

  useEffect(() => {
    if (waste.branchId) void waste.load(filters);
  }, [filters, waste.branchId]);

  if (!waste.loadingBranches && !waste.branchId) {
    return (
      <WorkspaceLayout>
        <BreadcrumbTwo />

        <SectionWrapper>
          <WasteBranchEmpty />
        </SectionWrapper>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <WasteHeader
          branches={waste.branches}
          branchId={waste.branchId}
          loadingBranches={waste.loadingBranches}
          canPickBranch={waste.canPickBranch}
          loading={waste.loading}
          onBranchChange={(value) => { setPage(0); waste.setBranchId(value); }}
          onCreate={waste.openCreate}
        />

        <WasteFilters
          search={search}
          categoryId={categoryId}
          reason={reason}
          dateFrom={dateFrom}
          dateTo={dateTo}
          categories={categories}
          onSearchChange={(value) => { setSearch(value); setPage(0); }}
          onCategoryChange={(value) => { setCategoryId(value); setPage(0); }}
          onReasonChange={(value) => { setReason(value); setPage(0); }}
          onDateFromChange={(value) => { setDateFrom(value); setPage(0); }}
          onDateToChange={(value) => { setDateTo(value); setPage(0); }}
        />

        {waste.loading ? (
          <WasteSkeleton />
        ) : waste.items.length === 0 ? (
          <WasteEmpty />
        ) : (
          <WasteTable items={waste.items} />
        )}

        {waste.page && (
          <BasicPagination
            currentPage={waste.page.number}
            totalPages={waste.page.totalPages}
            totalElements={waste.page.totalElements}
            size={waste.page.size}
            first={waste.page.number === 0}
            last={waste.page.number >= waste.page.totalPages - 1}
            onPageChange={setPage}
            onSizeChange={(value) => { setSize(value); setPage(0); }}
          />
        )}
      </SectionWrapper>

      <WasteFormModal
        isOpen={waste.modalOpen}
        form={waste.form}
        inventory={waste.inventory}
        selectedInventory={waste.selectedInventory}
        saving={waste.saving}
        onClose={waste.closeModal}
        onChange={waste.updateField}
        onSave={waste.confirmSave}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantWasteScreen;
